import {
    Position,
    SmartTriggerConfiguration,
    SmartTriggerProcessorNodeConfiguration,
    SmartTriggerUiConfiguration
} from "../types";
import {AuthContextType} from "../../../types/types";
import {ArrayUtils} from "../../../utils/arrayUtils";
import {Edge} from "reactflow";
import {EnvironmentRegistry} from "../../../services/registry/environmentRegistry";
import axios from "axios";
import {RepositoryProcessorData} from "../context/smartTriggerRepositoryProvider";

export function toSmartTriggerUIConfiguration(configuration: SmartTriggerConfiguration): SmartTriggerUiConfiguration {
    const uiConfiguration: SmartTriggerUiConfiguration = {
        nodes: [],
        edges: [],
        id: configuration.id,
        name: configuration.name,
        author: configuration.author,
        description: configuration.description,
        activeStatus: configuration.activeStatus
    }
    const processors: Record<string, SmartTriggerProcessorNodeConfiguration> = configuration.processors.reduce((config, processor) => {
        config[processor.id] = processor;
        return config;
    }, {} as Record<string, SmartTriggerProcessorNodeConfiguration>);

    let firstNode = configuration.processors.find(processor => processor.type === 'start');
    if (!firstNode) {
        throw new Error("Could not find start Node")
    }
    uiConfiguration.nodes.push(firstNode)
    getNodesAndEdges(processors, firstNode, uiConfiguration);
    return uiConfiguration;
}

export function toSmartTriggerConfiguration(configuration: SmartTriggerUiConfiguration): SmartTriggerConfiguration {
    return {
        id: configuration.id,
        name: configuration.name,
        author: configuration.author,
        description: configuration.description,
        tags: [],
        createdBy: configuration.author,
        activeStatus: 'ACTIVE',
        processors: configuration.nodes
    }
}

export function findNextPosition(currentPosition: Position = {x: 200, y: 0}): Position {
    return {
        x: currentPosition.x,
        y: currentPosition.y + 300
    }

}


function getNodesAndEdges(processors: Record<string, SmartTriggerProcessorNodeConfiguration>, currentProcessor: SmartTriggerProcessorNodeConfiguration, uiConfiguration: SmartTriggerUiConfiguration): SmartTriggerUiConfiguration {
    const nextNodes = getNextNodes(currentProcessor.data.next, processors);
    if (ArrayUtils.isEmpty(nextNodes)) {
        return uiConfiguration;
    }
    nextNodes.forEach(node => {
        node = {...node}
        node.data.previous = currentProcessor.id
        uiConfiguration.nodes.push(node);
        uiConfiguration.edges.push(createEdge(currentProcessor.id, node.id))
        getNodesAndEdges(processors, node, uiConfiguration);
    })
    return uiConfiguration;
}

export function createEdge(previous: string, next: string) {
    const type = next === 'addNode' ? 'straight' : 'buttonEdge'
    return {
        id: createEdgeId(previous, next),
        source: previous,
        target: next,
        type: type,
        animated: true
    }
}

export function toNodeMap(nodes: SmartTriggerProcessorNodeConfiguration[]): Record<string, SmartTriggerProcessorNodeConfiguration> {
    return nodes.reduce((config, processor) => {
        config[processor.id] = processor;
        return config;
    }, {} as Record<string, SmartTriggerProcessorNodeConfiguration>);
}

export function findEdge(previous: string, next: string, edges: Edge[]) {
    const id = createEdgeId(previous, next);
    const targetEdge = edges.find(edge => edge.id === id);
    if (!targetEdge) {
        throw new Error("Could not find the edge by id: " + id);
    }
    return targetEdge;
}


export function createEdgeId(previousNodeId: string, nextNodeId: string) {
    return previousNodeId + ":" + nextNodeId;
}


function getNextNodes(nextProcessorId: string | string[] | null, processors: Record<string, SmartTriggerProcessorNodeConfiguration>): SmartTriggerProcessorNodeConfiguration[] {
    if (!nextProcessorId) {
        return [];
    }
    if (Array.isArray(nextProcessorId)) {
        return nextProcessorId.map(id => getOrFailProcessor(processors, id));
    }
    return [getOrFailProcessor(processors, nextProcessorId)];
}

export function findNextNodes(nodeConfiguration: SmartTriggerProcessorNodeConfiguration, processors: Record<string, SmartTriggerProcessorNodeConfiguration>): SmartTriggerProcessorNodeConfiguration[] {
    return getNextNodes(nodeConfiguration.data.next, processors);
}

function getOrFailProcessor(processors: Record<string, SmartTriggerProcessorNodeConfiguration>, id: string) {
    const processor = processors[id];
    if (!processor) {
        throw new Error("Could not find proper processor")
    }
    return processor;
}

export function findProcessorNode(id: string, nodes: SmartTriggerProcessorNodeConfiguration[]): SmartTriggerProcessorNodeConfiguration {
    const processor = nodes.find(node => node.id === id)
    if (!processor) {
        throw new Error("Could not find processor with id:" + id)
    }
    return processor;
}

export function findNode(id: string, nodeMap: Record<string, SmartTriggerProcessorNodeConfiguration>): SmartTriggerProcessorNodeConfiguration {
    const node = nodeMap[id];
    if (!node) {
        throw new Error("Could not find node with id:" + id);
    }
    return node;
}

export function findPositionDifference(source: SmartTriggerProcessorNodeConfiguration, target: SmartTriggerProcessorNodeConfiguration): Position {
    return {
        x: target.position.x - source.position.x,
        y: target.position.y - source.position.y
    }
}

export function generateNewProcessorNode(previousNode: SmartTriggerProcessorNodeConfiguration, nextNode: SmartTriggerProcessorNodeConfiguration, newNode: SmartTriggerProcessorNodeConfiguration): SmartTriggerProcessorNodeConfiguration {

    return {
        ...newNode,
        position: findNextNodePosition(previousNode, nextNode)
    }
    // return {
    //     id: uuidv4(),
    //     data: {
    //         name: "unknown",
    //         description: "Please provide your descriptions here...",
    //         previous: previousNode.id,
    //         next: nextNode ? nextNode.id : null,
    //         isEdit: true,
    //     },
    //     position: findNextNodePosition(previousNode, nextNode),
    //     type: "script"
    // }
}

function findNextNodePosition(previousNode: SmartTriggerProcessorNodeConfiguration, nextNode: SmartTriggerProcessorNodeConfiguration) {
    let nextY = nextNode.position.y
    if (nextY <= previousNode.position.y) {
        nextY = previousNode.position.y + (previousNode.height || 0) + 100;
    }
    return {
        x: previousNode.position.x,
        y: nextY
    }
}


export async function getSmartTriggerConfiguration(authContext: AuthContextType, id: string): Promise<SmartTriggerConfiguration> {
    let smartTriggerServiceUrl = EnvironmentRegistry.getSmartTriggerServiceUrl(authContext.env);
    const smartTrigger = await axios.get(smartTriggerServiceUrl + "/v1/smart/triggers/" + id, {headers: EnvironmentRegistry.makeAuthHeadersFromContext(authContext)});
    return smartTrigger.data;
}

export async function getSmartTriggers(authContext: AuthContextType): Promise<SmartTriggerConfiguration[]> {
    const smartTriggerServiceUrl = EnvironmentRegistry.getSmartTriggerServiceUrl(authContext.env);
    const smartTriggers = await axios.get(smartTriggerServiceUrl + "/v1/smart/triggers", {headers: EnvironmentRegistry.makeAuthHeadersFromContext(authContext)})
    const triggers = smartTriggers.data.content as SmartTriggerConfiguration[];
    triggers.sort((a, b) => a.activeStatus === 'ACTIVE' ? -1 : 1);
    return triggers;
}

export async function getSmartTriggerRepositoryProcessors(authContext: AuthContextType) {
    let smartTriggerServiceUrl = EnvironmentRegistry.getSmartTriggerServiceUrl(authContext.env);
    const response = await axios.get(smartTriggerServiceUrl + "/v1/smart/trigger/repositories/processors", {headers: EnvironmentRegistry.makeAuthHeadersFromContext(authContext)});
    return response.data.content as RepositoryProcessorData[]
}

export async function pushSmartTrigger(authContext: AuthContextType, configuraiton: SmartTriggerConfiguration) {
    const url = EnvironmentRegistry.getSmartTriggerServiceUrl(authContext.env);
    return (await axios.put(url + "/v1/smart/triggers", configuraiton, {
        headers: EnvironmentRegistry.makeAuthHeadersFromContext(authContext)
    })).data;
}
